
import platformConfig from '~/config/config';
import URLS, { blacklist } from '~/URLS';

import Vue from 'vue';

// Services
import AuthService from '~/services/AuthService';
import BackgroundProcessService from '~/services/BackgroundProcessesService';
import WebinarMessageService from '~/services/WebinarMessageService';
import UserConsentContext, { CONSENT_IDENTIFIERS } from './services/UserConsentContext';
import { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from '~/services/types/identity';
import { CandidateProfileScorer } from '~/utils/candidate/CandidateProfileScorer';

// Components
import Navigation from './components/navigation/Navigation.vue';
import Sidebar from './components/navigation/Sidebar.vue';
import LogInModule from './shared/loginmodule/LogInModule.vue';
import NotificationModule from './components/smart/NotificationModule/NotificationModule.vue';
import ScrollToTop from './components/miscellaneous/ScrollToTop.vue';
import Chat from './components/chat/Chat.vue';
import IntercomChat from './components/miscellaneous/IntercomChat.vue';
import VFairHack from './components/miscellaneous/VFairHack.vue';
import BottomNotice from './shared/UserConsent/BottomNotice.vue';
import MinimizedVideoChat from './components/chat/MinimizedVideoChat.vue';
import Welcome from './views/welcome/Welcome.vue';
import ProfileImportModal from '~/components/profile/modals/ProfileImportModal.vue';

// Utils
import { HAS_LOGIN, HAS_CHAT, HAS_FORCED_LOGIN, IS_PROD } from '~/router';
import AnalyticService from './services/AnalyticService';
import { PortalTarget } from 'portal-vue';
import { CookieChangeOptions } from 'universal-cookie';
import { VFairUtils } from './views/vfairs/utils';

export default Vue.extend({
    components: {
        IntercomChat,
        Chat,
        Navigation,
        Sidebar,
        LogInModule,
        NotificationModule,
        ScrollToTop,
        MinimizedVideoChat,
        VFairHack,
        BottomNotice,
        PortalTarget,
        Welcome,
        ProfileImportModal,
    },
    data: () => ({
        isProd: IS_PROD,

        welcomeFlowStarted: false,
        welcomeFlowCompleted: false,
        showWelcomeFlow: false,

        showSidebar: false,
        blacklist: blacklist,
        hasLogin: HAS_LOGIN,
        hasChat: HAS_CHAT,
        hasConsented: false,
        showBottomConsentNotice: !UserConsentContext.hasAnswered(),
        cookies: UserConsentContext.cookie(),
        vfairMode: false,
    }),
    methods: {
        handleWelcomeFlowStarted() {
            this.welcomeFlowStarted = true;
        },
        handleWelcomeFlowCompleted() {
            this.welcomeFlowCompleted = true;
        },
        cookieCallback(options: CookieChangeOptions) {
            if (options.name === CONSENT_IDENTIFIERS.USER_CONSENT) {
                this.showBottomConsentNotice = false;
                this.cookies.removeChangeListener(this.cookieCallback);
            }
        },
        calculateShowWelcomeFlow() {
            // VFAIR HACK START
            const disabledWelcomeRoutes = platformConfig.rawConfig.disabledWelcomeRoutes || [];
            const isWelcomeDisabled = disabledWelcomeRoutes.some(route =>
                this.$route.path.startsWith(route)
            );
            if (!this.welcomeFlowCompleted && isWelcomeDisabled) {
                this.welcomeFlowCompleted = true; // if the route doesn't have a welcome page, don't show the welcome flow
                this.showWelcomeFlow = false;
                return;
            }
            // VFAIR HACK END

            if (this.welcomeFlowCompleted) this.showWelcomeFlow = false;
            // if the welcome flow is complete, don't show it again
            else if (this.welcomeFlowStarted)
                this.showWelcomeFlow = true; // if the welcome flow has started, show it
            else if (this.scorer === null) this.showWelcomeFlow = false;
            // if we don't have the data, don't show the welcome flow
            else if (this.scorer.getWelcomeFlowScore().progress < 100) this.showWelcomeFlow = true;
            // if the profile is not complete, show the welcome flow
            else {
                this.welcomeFlowCompleted = true; // if the profile is complete, don't show the welcome flow again
                this.showWelcomeFlow = false;
            }
        },
        checkVFairMode(event: Event) {
            const e: CustomEvent = event as CustomEvent;
            this.vfairMode = !!e?.detail?.isVFairMode;
        },
    },
    computed: {
        user(): User | null {
            return this.$store.getters['profileModule/getUser']();
        },
        candidate(): Candidate | null {
            return this.$store.getters['profileModule/getCandidate']();
        },
        workAuthorizations(): WorkAuthorization[] | null {
            return this.$store.getters['profileModule/getWorkAuthorizations']();
        },
        scorer(): CandidateProfileScorer | null {
            if (this.candidate && this.user && this.workAuthorizations)
                return new CandidateProfileScorer(
                    this.candidate,
                    this.user,
                    this.workAuthorizations
                );
            return null;
        },
        showNav() {
            return this.blacklist.filter(el => el === this.$route.path).length === 0;
        },
        showLogInModal() {
            return (
                this.$interfaces.logInModule.isOpen() &&
                !this.$interfaces.logInModule.getDisabledPopup()
            );
        },
        mountChat() {
            return this.hasChat && this.$interfaces.logInModule.isLoggedIn();
        },
    },
    watch: {
        scorer() {
            this.calculateShowWelcomeFlow();
        },
        welcomeFlowStarted() {
            this.calculateShowWelcomeFlow();
        },
        welcomeFlowCompleted() {
            this.calculateShowWelcomeFlow();
        },
    },
    async mounted() {
        // This condition is based on the fact that there is a "By continuing you accept our Terms of Service and Privacy Policy" in the register form
        if (!UserConsentContext.hasAnswered()) {
            this.showBottomConsentNotice = true;
            AnalyticService.disableGoogleAnalytics(true); // disable GA until consent is given
            this.cookies.addChangeListener(this.cookieCallback); // add listener to remove the consent notice when consent is given
        }

        // Initialize user-identity for logging
        if (AuthService.isAuthenticated()) {
            if (!UserConsentContext.hasAnswered()) UserConsentContext.acceptAll();
            await AuthService.setIsLoggedIn(true);
        }

        if (HAS_FORCED_LOGIN) {
            // Initialize logIn popup after X amount of seconds
            BackgroundProcessService.scheduleLogInPopup();
            this.$interfaces.logInModule.setForcedLogin();
        }

        const webinarMessageService = new WebinarMessageService();
        webinarMessageService.start();
        this.$interfaces.webinarMessageModule.setService(webinarMessageService);

        this.$store.dispatch('profileModule/initialize');

        window.addEventListener(VFairUtils.vfairModeEventName, this.checkVFairMode);
    },
    beforeDestroy() {
        window.removeEventListener(VFairUtils.vfairModeEventName, this.checkVFairMode);
    },
});
